import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, FILE_URL, BRANDS, STYLES, OCCASIONS, FOR_WHOM, COMBINES, CATEGORIES } from "../../lib/api";
import { Upload, X, ArrowLeft, Check, AlertCircle } from "lucide-react";

const EMPTY = {
  name: "", brand: "Cartier", custom_brand: "", code: "", category: "Colares", price: 0,
  status: "available", is_unique: false, is_new: false,
  purity: "", weight_g: null,
  size_anel: "", size_colares: "", size_pingente: "", size_brincos: "",
  size_pulseira: "", size_conjunto: "", size_diversos: "",
  style: [], occasion: [], sophistication: 3, for_whom: [], combines_with: [],
  short_description: "", full_description: "", highlight_phrase: "",
  images: [], body_images: [], collection: "",
};

// Which size field is shown for each category — one per category.
const SIZE_FIELD_BY_CAT = {
  "Anéis":     ["size_anel",     "Tamanho do anel"],
  "Colares":   ["size_colares",  "Tamanho do colar"],
  "Brincos":   ["size_brincos",  "Tamanho do brinco"],
  "Pingentes": ["size_pingente", "Tamanho do pingente"],
  "Pulseiras": ["size_pulseira", "Tamanho da pulseira"],
  "Outros":    ["size_diversos", "Tamanho — Diversos"],
};

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = id && id !== "novo";
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [codeStatus, setCodeStatus] = useState({ checking: false, available: null, msg: "" });
  const codeCheckTimer = useRef(null);

  useEffect(() => {
    if (isEdit) {
      api.get(`/products/${id}`).then((r) => {
        const data = { ...EMPTY, ...r.data };
        // If the saved brand isn't one of the predefined options (common for
        // bulk-imported catalogues with custom brands like "Antonio Bernardo",
        // "Jack Vartanian", etc.), coerce the form to show "Outra" + populate
        // the custom_brand input so the admin actually sees the saved value.
        if (data.brand && !BRANDS.includes(data.brand)) {
          data.custom_brand = data.custom_brand || data.brand;
          data.brand = "Outra";
        }
        setForm(data);
      }).catch(() => nav("/admin/produtos"));
    }
  }, [id, isEdit, nav]);

  // Real-time code validation
  useEffect(() => {
    if (codeCheckTimer.current) clearTimeout(codeCheckTimer.current);
    const code = (form.code || "").trim().toUpperCase();
    if (!code || code.length < 2) {
      setCodeStatus({ checking: false, available: null, msg: "" });
      return;
    }
    setCodeStatus({ checking: true, available: null, msg: "Verificando..." });
    codeCheckTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/products/code/check/${encodeURIComponent(code)}`, {
          params: isEdit ? { exclude_id: id } : {},
        });
        if (r.data.available) {
          setCodeStatus({ checking: false, available: true, msg: "Código disponível" });
        } else {
          setCodeStatus({ checking: false, available: false, msg: "Este código já está em uso" });
        }
      } catch {
        setCodeStatus({ checking: false, available: null, msg: "" });
      }
    }, 400);
    return () => { if (codeCheckTimer.current) clearTimeout(codeCheckTimer.current); };
  }, [form.code, id, isEdit]);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const toggleMulti = (k, v) => {
    const cur = form[k] || [];
    update(k, cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(r.data.path);
      }
      update("images", [...(form.images || []), ...paths]);
    } catch (e) {
      alert("Erro no upload: " + (e?.response?.data?.detail || ""));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (i) => {
    const next = [...form.images];
    const removed = next.splice(i, 1);
    update("images", next);
    // Also remove from body_images if it was marked.
    const path = removed[0];
    if (path && (form.body_images || []).includes(path)) {
      update("body_images", (form.body_images || []).filter((p) => p !== path));
    }
  };

  const toggleBodyImage = (path) => {
    const cur = form.body_images || [];
    update("body_images", cur.includes(path) ? cur.filter((p) => p !== path) : [...cur, path]);
  };

  const submit = async (e) => {
    e.preventDefault();
    // M229 — custom_brand é opcional. Quando admin selecionar "Outra" mas
    // não preencher o nome, o produto é salvo apenas com brand="Outra" e o
    // catálogo exibe "Outras grifes" como rótulo.
    if (codeStatus.available === false) {
      alert("Código duplicado — escolha outro");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: (form.code || "").trim().toUpperCase(),
        price: Number(form.price),
        weight_g: form.weight_g ? Number(form.weight_g) : null,
        sophistication: Number(form.sophistication),
      };
      if (isEdit) await api.put(`/products/${id}`, payload);
      else await api.post("/products", payload);
      nav("/admin/produtos");
    } catch (e) {
      alert("Erro ao salvar: " + (e?.response?.data?.detail || ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-product-form">
      <Link to="/admin/produtos" className="overline text-[#6B6B6B] inline-flex items-center gap-2 mb-6">
        <ArrowLeft size={14}/> Voltar
      </Link>
      <h1 className="font-display text-4xl mb-10">{isEdit ? "Editar peça" : "Nova peça"}</h1>

      <form onSubmit={submit} className="space-y-10">
        {/* Identificação */}
        <Section title="Identificação">
          <Field label="Código da peça (obrigatório)">
            <div className="relative">
              <input
                data-testid="form-code"
                required
                value={form.code || ""}
                onChange={(e) => update("code", e.target.value.toUpperCase())}
                className="input pr-12"
                placeholder="Ex: PJ-0042"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {codeStatus.checking && <span className="text-xs text-[#A8A8A8]">…</span>}
                {!codeStatus.checking && codeStatus.available === true && <Check size={16} className="text-green-600"/>}
                {!codeStatus.checking && codeStatus.available === false && <AlertCircle size={16} className="text-red-600"/>}
              </div>
            </div>
            {codeStatus.msg && (
              <p
                data-testid="code-status-msg"
                className={`text-xs mt-1 ${codeStatus.available === false ? "text-red-600" : codeStatus.available === true ? "text-green-700" : "text-[#A8A8A8]"}`}
              >
                {codeStatus.msg}
              </p>
            )}
          </Field>

          <Field label="Nome da peça">
            <input data-testid="form-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="input" />
          </Field>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Grife / Marca">
              <select data-testid="form-brand" value={form.brand} onChange={(e) => update("brand", e.target.value)} className="input">
                {BRANDS.map((b) => <option key={b}>{b}</option>)}
              </select>
              {form.brand === "Outra" && (
                <div className="mt-2">
                  <label className="overline text-[#A8A8A8] mb-1 block">Qual marca? <span className="text-[#A8A8A8] normal-case tracking-normal italic ml-1">(opcional)</span></label>
                  <input
                    data-testid="form-custom-brand"
                    value={form.custom_brand || ""}
                    onChange={(e) => update("custom_brand", e.target.value)}
                    className="input"
                    placeholder="Digite o nome da grife"
                  />
                </div>
              )}
            </Field>
            <Field label="Categoria">
              <select data-testid="form-category" value={form.category} onChange={(e) => update("category", e.target.value)} className="input">
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Preço (R$)">
              <input data-testid="form-price" type="number" step="0.01" required value={form.price} onChange={(e) => update("price", e.target.value)} className="input" />
            </Field>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Status">
              <select data-testid="form-status" value={form.status} onChange={(e) => update("status", e.target.value)} className="input">
                <option value="available">Disponível</option>
                <option value="sold">Vendida</option>
                <option value="paused">Pausada</option>
              </select>
              {form.status === "sold" && (
                <p className="text-[11px] text-[#A8A8A8] mt-1">
                  Peças vendidas são removidas automaticamente do sistema 30 dias após esta data.
                </p>
              )}
            </Field>
            <Field label="Marcadores">
              <label className="flex items-center gap-2 mt-2 mb-1">
                <input data-testid="form-isunique" type="checkbox" checked={!!form.is_unique} onChange={(e) => update("is_unique", e.target.checked)} />
                Peça Única (exibe selo)
              </label>
              <label className="flex items-center gap-2">
                <input data-testid="form-isnew" type="checkbox" checked={!!form.is_new} onChange={(e) => update("is_new", e.target.checked)} />
                Novidade (exibe em /novidades por 30 dias)
              </label>
            </Field>
          </div>
        </Section>

        {/* Especificações */}
        <Section title="Especificações técnicas">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Peso da peça (g)">
              <input
                data-testid="form-weight"
                type="number"
                step="0.1"
                value={form.weight_g || ""}
                onChange={(e) => update("weight_g", e.target.value)}
                className="input"
                placeholder="Ex: 3.2"
              />
            </Field>
            {(() => {
              const cfg = SIZE_FIELD_BY_CAT[form.category];
              if (!cfg) return null;
              const [fieldName, label] = cfg;
              return (
                <Field label={label}>
                  <input
                    data-testid={`form-${fieldName}`}
                    value={form[fieldName] || ""}
                    onChange={(e) => update(fieldName, e.target.value)}
                    className="input"
                    placeholder="Ex: aro 16 · 45 cm · 1,2 cm de diâmetro"
                  />
                </Field>
              );
            })()}
          </div>
        </Section>

        {/* Estilo */}
        <Section title="Classificações de estilo (quiz)">
          <ChipGroup label="Estilo (até 2)" options={STYLES} values={form.style} onToggle={(v) => toggleMulti("style", v)} />
          <ChipGroup label="Ocasião" options={OCCASIONS} values={form.occasion} onToggle={(v) => toggleMulti("occasion", v)} />
          <Field label="Sofisticação (1 sutil — 5 statement)">
            <input data-testid="form-soph" type="range" min="1" max="5" value={form.sophistication} onChange={(e) => update("sophistication", e.target.value)} className="w-full accent-[#B8A88A]" />
            <p className="text-xs text-[#6B6B6B] mt-1">Atual: {form.sophistication}</p>
          </Field>
          <ChipGroup label="Para quem" options={FOR_WHOM} values={form.for_whom} onToggle={(v) => toggleMulti("for_whom", v)} />
          <ChipGroup label="Combina com" options={COMBINES} values={form.combines_with} onToggle={(v) => toggleMulti("combines_with", v)} />
        </Section>

        {/* Conteúdo */}
        <Section title="Conteúdo editorial">
          <Field label="Descrição curta (cards e PDP)">
            <textarea data-testid="form-short-desc" rows={2} value={form.short_description} onChange={(e) => update("short_description", e.target.value)} className="input" />
          </Field>
          <Field label="Descrição completa (história, inspiração)">
            <textarea data-testid="form-full-desc" rows={5} value={form.full_description} onChange={(e) => update("full_description", e.target.value)} className="input" />
          </Field>
          <Field label="Frase de destaque (1 linha poética usada pela IA)">
            <input data-testid="form-phrase" value={form.highlight_phrase} onChange={(e) => update("highlight_phrase", e.target.value)} className="input" />
          </Field>
          <Field label="Coleção/Série">
            <input value={form.collection || ""} onChange={(e) => update("collection", e.target.value)} className="input" />
          </Field>
        </Section>

        {/* Imagens */}
        <Section title="Fotos">
          <p className="text-xs text-[#6B6B6B] mb-3">
            Marque <strong>Foto no corpo</strong> nas imagens que mostram a peça sendo usada por uma modelo —
            essas imagens serão usadas como referência de escala real no Try-On.
          </p>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-4">
            {(form.images || []).map((p, i) => {
              const isBody = (form.body_images || []).includes(p);
              return (
                <div key={p} className="relative" data-testid={`product-image-${i}`}>
                  <div className="relative aspect-square bg-[#FAF9F7]">
                    <img src={FILE_URL(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-white p-1" aria-label="Remover">
                      <X size={12}/>
                    </button>
                    {i === 0 && <span className="absolute bottom-1 left-1 bg-[#B8A88A] text-white text-[10px] px-2">Capa</span>}
                    {isBody && <span className="absolute top-1 left-1 bg-[#1A1A1A] text-white text-[10px] px-2 py-0.5">No corpo</span>}
                  </div>
                  <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer text-[11px] text-[#4A4A4A] select-none">
                    <input
                      type="checkbox"
                      checked={isBody}
                      onChange={() => toggleBodyImage(p)}
                      data-testid={`body-checkbox-${i}`}
                      className="cursor-pointer"
                    />
                    Foto no corpo
                  </label>
                </div>
              );
            })}
          </div>
          <label className="btn-secondary cursor-pointer inline-flex" data-testid="upload-images-btn">
            <Upload size={14} className="mr-2"/> {uploading ? "Enviando..." : "Adicionar fotos"}
            <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
          </label>
        </Section>

        <div className="flex gap-3 pt-6 border-t border-[#E8E4DF]">
          <button
            type="submit"
            disabled={saving || codeStatus.available === false}
            data-testid="save-product-btn"
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Salvando..." : (isEdit ? "Salvar alterações" : "Criar peça")}
          </button>
          <Link to="/admin/produtos" className="btn-secondary">Cancelar</Link>
        </div>
      </form>

      <style>{`.input { width: 100%; border: 1px solid #E8E4DF; padding: 10px 14px; background: white; }
        .input:focus { outline: none; border-color: #B8A88A; }`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white p-6 md:p-8 space-y-5">
      <h3 className="font-display text-xl border-b border-[#E8E4DF] pb-3">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline text-[#A8A8A8] mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function ChipGroup({ label, options, values, onToggle }) {
  return (
    <div>
      <label className="overline text-[#A8A8A8] mb-2 block">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const selected = (values || []).includes(o);
          return (
            <button
              type="button"
              key={o}
              onClick={() => onToggle(o)}
              className={`px-3 py-1.5 text-xs border ${selected ? "border-[#B8A88A] bg-[#F5EFE8]" : "border-[#E8E4DF]"}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
